<template>
  <v-dialog v-model="show" max-width="500">
    <v-card>
      <v-card-title class="text-h5">Delete Sales</v-card-title>
      
      <v-card-text>
        <p class="text-body-1 mb-4">
          Please upload a CSV file with sale IDs to delete. The file should:
        </p>
        <ul class="text-body-2 mb-4 px-4">
          <li>Have a single column</li>
          <li>Include a header row (will be skipped)</li>
          <li>Contain sale IDs in subsequent rows</li>
        </ul>
        
        <v-file-input
          v-model="file"
          label="Upload CSV"
          accept=".csv"
          :loading="uploading"
          @change="handleFileUpload"
        ></v-file-input>

        <div v-if="saleIds.length > 0" class="mt-4">
          <div class="text-body-2">Found {{ saleIds.length }} sales to delete</div>
        </div>

        <v-alert
          v-if="error"
          type="error"
          class="mt-4"
          density="compact"
        >
          {{ error }}
        </v-alert>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="grey"
          variant="text"
          @click="show = false"
        >
          Cancel
        </v-btn>
        <v-btn
          color="error"
          :loading="loading"
          :disabled="saleIds.length === 0"
          @click="handleDelete"
        >
          Delete Sales
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, defineProps, defineEmits, watch } from 'vue'
import { salesService } from '@/services/salesService'

const props = defineProps({
  modelValue: Boolean
})

const emit = defineEmits(['update:modelValue', 'success'])

const show = ref(props.modelValue)
const file = ref(null)
const error = ref('')
const loading = ref(false)
const uploading = ref(false)
const saleIds = ref([])

watch(() => props.modelValue, (val) => {
  show.value = val
  if (!val) {
    // Reset state when modal closes
    file.value = null
    error.value = ''
    saleIds.value = []
  }
})

watch(show, (val) => {
  emit('update:modelValue', val)
})

const handleFileUpload = async () => {
  if (!file.value) {
    saleIds.value = []
    return
  }

  uploading.value = true
  saleIds.value = []
  error.value = ''

  try {
    const text = await file.value.text()
    const rows = text.split('\n').map(row => row.trim())
    
    // Skip header row and empty rows
    const ids = rows.slice(1)
      .filter(row => row)
      .map(row => row.replace(/["',]/g, '').trim())
      .filter(id => id.length > 0)
    
    // Validate IDs
    if (ids.some(id => isNaN(id))) {
      throw new Error('File contains invalid sale IDs: ' + ids.filter(id => isNaN(id)).join(', '))
    }
    
    saleIds.value = ids
  } catch (err) {
    error.value = err.message || 'Failed to process file'
    file.value = null
  } finally {
    uploading.value = false
  }
}

const handleDelete = async () => {
  if (saleIds.value.length === 0) return
  
  loading.value = true
  try {
    await salesService.deleteSales(saleIds.value)
    emit('success', saleIds.value.length)
    show.value = false
  } catch (err) {
    error.value = 'Failed to delete sales'
    console.error('Error deleting sales:', err)
  } finally {
    loading.value = false
  }
}
</script> 